import React, { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Search, Check, X, Lock, Wand2, Loader2, UserCircle, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const CAP_LABELS = {
  create_project:   'Create project',
  delete_project:   'Delete project',
  edit_chapters:    'Edit chapters',
  edit_variables:   'Edit variables',
  generate_ai:      'Generate with AI',
  invite_members:   'Invite members',
  view_billing:     'View billing',
  restore_versions: 'Restore chapter versions',
  branch_versions:  'Branch chapter versions',
};

/** Permission Gating Matrix Simulator.
 *
 *  An admin picks a user (email or user_id) + optional project_id and sees,
 *  capability-by-capability, which UI controls the user would see lit up
 *  vs. greyed out — answering "why can't Sarah click Draft chapter?" in one
 *  glance. */
export default function PermissionPreview() {
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [userId, setUserId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    Promise.all([
      adminApi.users().then((r) => r.users || []).catch(() => []),
      adminApi.browse('projects', { limit: 200 }).then((r) => r.docs || []).catch(() => []),
    ]).then(([u, p]) => {
      setUsers(u);
      setProjects(p);
    });
  }, []);

  const filteredUsers = useMemo(() => {
    if (!filter.trim()) return users.slice(0, 30);
    const q = filter.toLowerCase();
    return users.filter((u) =>
      (u.email || '').toLowerCase().includes(q) ||
      (u.name || '').toLowerCase().includes(q) ||
      (u.user_id || '').toLowerCase().includes(q),
    ).slice(0, 30);
  }, [users, filter]);

  const runPreview = async (uid = userId, pid = projectId) => {
    if (!uid) { toast.warning('Pick a user first.'); return; }
    setBusy(true);
    try {
      const r = await adminApi.permissionPreview(uid, pid || null);
      setPreview(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed.');
      setPreview(null);
    } finally { setBusy(false); }
  };

  const pickUser = (u) => {
    setUserId(u.user_id);
    runPreview(u.user_id, projectId);
  };

  const pickProject = (pid) => {
    setProjectId(pid);
    if (userId) runPreview(userId, pid);
  };

  return (
    <div className="mt-8 rounded-md border border-border bg-card">
      <div className="border-b border-border bg-panel-header/40 px-3 py-2">
        <div className="flex items-center gap-2">
          <Wand2 className="h-4 w-4 text-primary" />
          <div>
            <div className="text-[13px] font-semibold">Permission Gating Matrix Simulator</div>
            <div className="text-[11px] text-muted-foreground">
              Pick a user (and optionally a project) to preview which Generate/Edit/Delete buttons light up vs. grey out for them.
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-4 p-3 lg:grid-cols-3">
        {/* User picker */}
        <div className="lg:col-span-1">
          <Label className="text-[11px]">User</Label>
          <div className="relative mt-1">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="search email or name…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="perm-preview-user-search"
              className="h-8 pl-7 text-[12px]"
            />
          </div>
          <div className="mt-2 max-h-64 overflow-auto scroll-thin rounded-sm border border-border bg-background/50">
            {filteredUsers.length === 0 && (
              <div className="px-2 py-3 text-center text-[11px] text-muted-foreground">No users match.</div>
            )}
            {filteredUsers.map((u) => (
              <button
                key={u.user_id}
                onClick={() => pickUser(u)}
                data-testid={`perm-preview-user-${u.user_id}`}
                className={cn(
                  'flex w-full items-center gap-2 border-b border-border px-2 py-1.5 text-left text-[11.5px] hover:bg-secondary',
                  userId === u.user_id && 'bg-primary/10',
                )}
              >
                <UserCircle className="h-3.5 w-3.5 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium">{u.email || u.user_id}</div>
                  <div className="truncate text-[10.5px] text-muted-foreground">
                    {(u.name || '—')} · role: <b>{u.role || 'ordinary'}</b>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Project picker */}
        <div className="lg:col-span-1">
          <Label className="text-[11px]">Project (optional)</Label>
          <select
            value={projectId}
            onChange={(e) => pickProject(e.target.value)}
            data-testid="perm-preview-project-select"
            className="mt-1 h-8 w-full rounded-sm border border-border bg-background px-2 text-[12px]"
          >
            <option value="">— (no project) — global permissions only</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name} · {p.id?.slice(0, 8)}…</option>
            ))}
          </select>
          {preview?.project && (
            <div className="mt-3 rounded-sm border border-border bg-background/40 px-2 py-2 text-[11px]">
              <div className="flex items-center gap-1.5 text-foreground">
                <FolderOpen className="h-3.5 w-3.5 text-primary" />
                <span className="truncate font-medium">{preview.project.name}</span>
              </div>
              <div className="mt-1 text-muted-foreground">
                Project role: <b className="text-foreground">{preview.project.is_owner ? 'owner (editor)' : preview.project.project_role || 'NOT a member'}</b>
              </div>
              {preview.project.project_permissions?.length > 0 && (
                <div className="mt-0.5 font-mono-stat text-[10.5px] text-muted-foreground">
                  Project perms: {preview.project.project_permissions.join(', ')}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Result column */}
        <div className="lg:col-span-1">
          <Label className="text-[11px]">Resolved capabilities</Label>
          {busy && (
            <div className="mt-2 flex items-center gap-2 text-[11.5px] text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" /> Computing…
            </div>
          )}
          {!busy && !preview && (
            <div className="mt-2 rounded-sm border border-dashed border-border px-3 py-6 text-center text-[11px] text-muted-foreground">
              Pick a user to preview their button-level permissions.
            </div>
          )}
          {!busy && preview && (
            <>
              <div className="mt-1 rounded-sm border border-border bg-background/40 px-2 py-2 text-[11px]">
                <div className="font-medium">{preview.user.email || preview.user.user_id}</div>
                <div className="text-muted-foreground">Global role: <b className="text-foreground">{preview.user.global_role}</b></div>
              </div>
              <ul className="mt-2 space-y-1">
                {Object.entries(preview.capabilities).map(([cap, allowed]) => (
                  <li
                    key={cap}
                    data-testid={`perm-preview-cap-${cap}`}
                    className={cn(
                      'flex items-center justify-between rounded-sm border px-2 py-1 text-[11.5px]',
                      allowed
                        ? 'border-success/30 bg-success/5 text-foreground'
                        : 'border-border bg-secondary/40 text-muted-foreground',
                    )}
                  >
                    <span className="flex items-center gap-1.5">
                      {allowed
                        ? <Check className="h-3.5 w-3.5 text-success" />
                        : <Lock className="h-3.5 w-3.5 text-muted-foreground/70" />}
                      {CAP_LABELS[cap] || cap}
                    </span>
                    <span className={cn('font-mono-stat text-[10.5px]', allowed ? 'text-success' : 'text-muted-foreground')}>
                      {allowed ? 'LIT' : 'GREY'}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>

      {preview && (
        <div className="border-t border-border bg-panel-header/30 px-3 py-2 text-[10.5px] text-muted-foreground">
          <X className="mr-1 inline h-3 w-3" /> Greyed capabilities will return a 403 from the server even if the
          user crafts a direct API call. The matrix is computed identically on the backend.
        </div>
      )}
    </div>
  );
}
